import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { VIEW_CHAT, VIEW_MODERATION } from './EsConstants';
import { esDayMonClock } from './EsDate';
import { esGuardianKindName } from './EsGuardian';
import { ES_DELETED } from './EsNicknames';
import {
    esBadgeText, esNotificationList, esNotificationRead, esNotificationsAllRead, esNotificationsKnown,
    useEsNotificationCount,
} from './EsNotifications';
import { useEsStrings } from './EsStrings';

/**
 * The bell at the right of the nav bar's control row, beside the account circle: a mention, a
 * Message, a reply to your post, a reaction on one, an invite to a chat (esback's EsNotifyService),
 * a friending, which opens the friend's profile card in the Commons and asks for one back until it
 * is returned, and for a moderator, a report, which opens the moderation view. Drawn only for a
 * signed-in reader while the Commons is on (rolloutEsChat).
 *
 * <p>The badge is the unread count, 99+ past 99, laid over the disc rather than beside it, so the
 * row's width never moves with it and the bar's fold measures what it always did. The dropdown
 * reads the list as it opens, newest first; a row is read as it is opened, and it opens its source
 * -- the topic, the table's chat, or the Message -- where the Commons places the reader at the first
 * line they have not read. Mark all read empties the badge, and is drawn only while something is
 * unread: a link left on the panel with nothing to do read as one that did nothing (B5).
 *
 * <p>Below sm the panel spans the window under the bar, gutter to gutter, rather than hanging off
 * the disc, which at 390 px would run past the left edge. Dismissed by a click elsewhere, Escape,
 * or a navigation, as the account menu is.
 */
export default function EsNotificationBell() {
    const t = useEsStrings();
    const navigate = useNavigate();
    const aPath = useLocation().pathname;
    const count = useEsNotificationCount( true );
    const [ open, setOpen ] = useState( false );
    const [ list, setList ] = useState( null );
    const [ failed, setFailed ] = useState( false );
    const box = useRef( null );

    useEffect( () => {
        setOpen( false );
    }, [ aPath ] );

    const load = () => {
        setFailed( false );
        return esNotificationList()
            .then( ( pList ) => {
                setList( pList?.items ?? [] );
                esNotificationsKnown( pList?.unread );
            } )
            .catch( () => setFailed( true ) );
    };

    useEffect( () => {
        if ( !open ) return undefined;
        void load();

        const aClick = ( pEvent ) => {
            if ( box.current && !box.current.contains( pEvent.target ) ) setOpen( false );
        };
        const aKey = ( pEvent ) => {
            if ( pEvent.key === 'Escape' ) setOpen( false );
        };
        document.addEventListener( 'mousedown', aClick );
        document.addEventListener( 'keydown', aKey );
        return () => {
            document.removeEventListener( 'mousedown', aClick );
            document.removeEventListener( 'keydown', aKey );
        };
    }, [ open ] );

    const go = ( pItem ) => {
        // Read as it is opened; the badge hears it from the answer, and a mark that did not land
        // is marked again by the Commons once the line is on screen.
        if ( !pItem.read ) {
            esNotificationRead( pItem.id ).then( load ).catch( () => {} );
        }
        setOpen( false );
        if ( pItem.kind === 'REPORT' || pItem.kind === 'HELD' ) {
            navigate( VIEW_MODERATION );
            return;
        }
        // The card is where Friend is; the Commons opens it from the history state (EsCommons).
        if ( pItem.kind === 'FRIEND' ) {
            navigate( VIEW_CHAT, { state: { esCommonsProfile: pItem.actorId } } );
            return;
        }
        // The post rides in the history state, so the Commons opens on it (EsChat's jumpTo).
        navigate( VIEW_CHAT + '/' + pItem.topicKey, pItem.postId ? { state: { esChatPost: pItem.postId } } : undefined );
    };

    const markAll = () => {
        esNotificationsAllRead().then( load ).catch( () => setFailed( true ) );
    };

    // Every notice is an account's doing; one whose account is gone is signed as a gone author is.
    const aWho = ( pItem ) => pItem.actorName || ES_DELETED;
    const aPlace = ( pItem ) => pItem.place || t( 'nav-notifications-a-topic', 'a topic' );

    const headline = ( pItem ) => {
        switch ( pItem.kind ) {
            case 'MESSAGE':
                return t( 'nav-notifications-message', '{who} sent you {count, plural, one {a message} other {# messages}}',
                    { who: aWho( pItem ), count: pItem.count } );
            case 'CALL':
                return t( 'nav-notifications-call', '{who} called you', { who: aWho( pItem ) } );
            case 'REPLY':
                return t( 'nav-notifications-reply', '{who} replied to your post in {place}', { who: aWho( pItem ), place: aPlace( pItem ) } );
            case 'REPORT':
                return t( 'nav-notifications-report', '{who} reported {what}', { who: aWho( pItem ), what: esGuardianKindName( pItem.reported, t ) } );
            case 'HELD':
                return t( 'nav-notifications-held', 'The filter held {what} for review', { what: esGuardianKindName( pItem.reported, t ) } );
            case 'FRIEND':
                return t( 'nav-notifications-friend', '{who} added you as a friend', { who: aWho( pItem ) } );
            case 'INVITE':
                return t( 'nav-notifications-invite', '{who} invited you to {place}', { who: aWho( pItem ), place: aPlace( pItem ) } );
            case 'REACTION':
                return pItem.count > 1
                    ? t( 'nav-notifications-reactions', '{count} reactions to your post in {place}', { count: pItem.count, place: aPlace( pItem ) } )
                    : t( 'nav-notifications-reaction', '{who} reacted to your post in {place}', { who: aWho( pItem ), place: aPlace( pItem ) } );
            default:
                return t( 'nav-notifications-mention', '{who} mentioned you in {place}', { who: aWho( pItem ), place: aPlace( pItem ) } );
        }
    };

    const aLabel = count > 0
        ? t( 'nav-notifications-label-unread', 'Notifications, {count} unread', { count } )
        : t( 'nav-notifications-label', 'Notifications' );
    const aUnreadShown = ( list ?? [] ).some( ( pItem ) => !pItem.read ) || count > 0;

    return (
        <div ref={ box } className="relative" data-testid="es-nav-notifications">
            <button
                type="button"
                data-testid="es-nav-notifications-bell"
                aria-label={ aLabel }
                aria-expanded={ open }
                aria-haspopup="true"
                data-tip={ t( 'nav-notifications-label', 'Notifications' ) }
                onClick={ () => setOpen( ( pWas ) => !pWas ) }
                className="esNavDisc relative ms-1 flex sm:ms-2 cursor-pointer items-center justify-center rounded-full text-nav-glyph bg-nav-disc hover:bg-nav-disc-hover">
                <i className="pi pi-bell text-lg leading-none" aria-hidden="true" />
                { count > 0 &&
                    <span data-testid="es-nav-notifications-badge" aria-hidden="true"
                          className="pointer-events-none absolute -end-1 -top-1 min-w-[1.125rem] rounded-full bg-danger px-1 text-center text-[10px] font-bold leading-[1.125rem] text-white ring-2 ring-chrome">
                        { esBadgeText( count ) }
                    </span> }
            </button>

            <div
                data-testid="es-nav-notifications-menu"
                aria-hidden={ !open }
                className={ ( open ? 'flex' : 'hidden' )
                    + ' z-[99999] flex-col rounded-lg border border-nav-menu-edge bg-panel text-sm text-title shadow-lg'
                    + ' max-sm:fixed max-sm:inset-x-4 max-sm:top-[calc(var(--es-nav-h,4rem)+0.5rem)]'
                    + ' sm:absolute sm:inset-e-0 sm:top-[calc(100%+0.5rem)] sm:w-80'
                    + ' max-h-[min(28rem,calc(100dvh-6rem))]' }>
                <div className="flex items-center justify-between gap-2 border-b border-edge px-4 py-2">
                    <span className="esLabel">{ t( 'nav-notifications-label', 'Notifications' ) }</span>
                    { aUnreadShown &&
                        <button type="button" data-testid="es-nav-notifications-mark-all" onClick={ markAll }
                                className="text-xs text-accent hover:underline">
                            { t( 'nav-notifications-mark-all', 'Mark all read' ) }
                        </button> }
                </div>

                { failed &&
                    <p role="alert" data-testid="es-nav-notifications-error" className="px-4 py-3 text-muted">
                        { t( 'nav-notifications-error', 'Notifications did not load.' ) }
                    </p> }

                { !failed && list !== null && list.length === 0 &&
                    <p data-testid="es-nav-notifications-empty" className="px-4 py-3 text-muted">
                        { t( 'nav-notifications-empty', 'Nothing yet. A mention, a Message, or a reply to your post shows here.' ) }
                    </p> }

                { !failed && list !== null && list.length > 0 &&
                    <ul data-testid="es-nav-notifications-list" className="min-h-0 overflow-y-auto py-1">
                        { list.map( ( pItem ) => (
                            <li key={ pItem.id }>
                                <button type="button" data-testid={ 'es-nav-notifications-item-' + pItem.id }
                                        data-read={ pItem.read ? 'true' : 'false' }
                                        onClick={ () => go( pItem ) }
                                        className={ 'flex w-full flex-col items-start gap-0.5 px-4 py-2 text-start hover:bg-nav-disc-hover'
                                            + ( pItem.read ? ' text-muted' : '' ) }>
                                    <span className={ 'flex w-full items-baseline gap-2' + ( pItem.read ? '' : ' font-semibold' ) }>
                                        { !pItem.read && <span className="size-2 shrink-0 self-center rounded-full bg-accent" aria-hidden="true" /> }
                                        <span className="min-w-0 grow">{ headline( pItem ) }</span>
                                        <span className="shrink-0 text-xs font-normal text-muted">{ esDayMonClock( pItem.at ) }</span>
                                    </span>
                                    { pItem.text &&
                                        <span className="line-clamp-2 w-full break-words text-xs text-muted" data-testid={ 'es-nav-notifications-text-' + pItem.id }>
                                            { pItem.text }
                                        </span> }
                                    { pItem.kind === 'FRIEND' && pItem.friends === false &&
                                        <span className="text-xs text-accent" data-testid={ 'es-nav-notifications-friend-back-' + pItem.id }>
                                            { t( 'nav-notifications-friend-back', 'Friend them back?' ) }
                                        </span> }
                                </button>
                            </li>
                        ) ) }
                    </ul> }
            </div>
        </div>
    );
}
